import React, { memo, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { Nullable } from '~/eds/src/types/Nullable';
import { Option } from '~/eds/src/types/Option';

export type DeleteDataType = {
  comment: string;
  reason: Nullable<Reason>;
};
export type Reason = 'duplicate' | 'mistake' | 'other';

interface Props extends DeleteDataType {
  type: 'document' | 'folder';
  selectedCount: number;
  onChange: (params: DeleteDataType) => void;
  shouldShowDuplicateWarning?: boolean;
}
const getOptions = (intl: IntlShape): Option<Reason>[] => [
  {
    label: intl.formatMessage({
      id: 'DeleteAction.duplicateOption',
      defaultMessage: 'Duplicate',
      description: 'Reason option for duplicate items',
    }),
    value: 'duplicate',
  },
  {
    label: intl.formatMessage({
      id: 'DeleteAction.addedByMistakeOption',
      defaultMessage: 'Added by mistake',
      description: 'Reason option for items added by mistake',
    }),
    value: 'mistake',
  },
  {
    label: intl.formatMessage({
      id: 'DeleteAction.otherOption',
      defaultMessage: 'Other',
      description: 'Reason option for other reasons',
    }),
    value: 'other',
  },
];
const CHAR_LIMIT = 100;
export const DeleteAction = memo(
  ({
    comment: deleteComment,
    reason: deleteReason,
    selectedCount,
    shouldShowDuplicateWarning = true,
    onChange,
  }: Props) => {
    const intl = useIntl();

    const handleCommentChange = (updatedComment: string | null): void => {
      if (!updatedComment) {
        onChange({
          reason: deleteReason,
          comment: '',
        });
      }
      if (updatedComment && updatedComment?.length <= CHAR_LIMIT) {
        onChange({
          reason: deleteReason,
          comment: updatedComment.slice(0, CHAR_LIMIT),
        });
      }
    };

    useEffect(() => {
      onChange({
        reason: deleteReason,
        comment: deleteComment,
      });
    }, [deleteReason, deleteComment]);

    const handleReasonChange = (updatedReason: Nullable<Reason>): void => {
      onChange({
        reason: updatedReason,
        comment: deleteComment,
      });
    };

    return (
      <Layout direction="column" spacing={4} p={4}>
        {shouldShowDuplicateWarning && (
          <Markdown
            text={intl.formatMessage(
              {
                id: 'DeleteAction.warningMessage',
                defaultMessage:
                  'Only items you have permission to edit will be deleted. This action cannot be undone. Deleting items: {count}',
                description:
                  'Warning message about permanent deletion. Count is a number: I.E 3, 4. Example: count = "3"',
              },
              {
                count: selectedCount,
              },
            )}
          />
        )}
        <Layout direction="column" spacing={2}>
          <Text variant="body-medium">
            <FormattedMessage
              id="DeleteAction.reasonPrompt"
              defaultMessage="Please provide a reason to delete the selected {count, plural, one {item} other {items}}"
              description="Prompt asking for deletion reason, type is a document or folder."
              values={{ count: selectedCount }}
            />
          </Text>
          <RadioGroup
            name="deleteReason"
            options={getOptions(intl)}
            value={deleteReason}
            onChange={handleReasonChange}
          />
        </Layout>
        <Layout direction="column">
          <Layout direction="column" spacing={2}>
            <Text variant="body-medium">
              {deleteReason === 'other' ? (
                <FormattedMessage
                  id="DeleteAction.tellUsMore.required"
                  defaultMessage="Tell us more (Required)"
                  description="Prompt for additional details"
                />
              ) : (
                <FormattedMessage
                  id="DeleteAction.tellUsMore.optional"
                  defaultMessage="Tell us more (Optional)"
                  description="Prompt for additional details, optional"
                />
              )}
            </Text>
            <TextArea
              placeholder={intl.formatMessage({
                id: 'DeleteAction.reasonPlaceholder',
                defaultMessage: 'Your reason',
                description: 'Placeholder text for reason input',
              })}
              value={deleteComment}
              onChange={handleCommentChange}
              name="reason"
            />
          </Layout>
          <Text variant="tiny" color="text.secondary">
            <FormattedMessage
              id="DeleteAction.characterCount"
              defaultMessage="{currentLength} / {maxLength} characters"
              description="Character count display, currantLength and maxLength are numbers. Example: currentLength = '45', maxLength = '500'"
              values={{
                currentLength: deleteComment.length,
                maxLength: CHAR_LIMIT,
              }}
            />
          </Text>
        </Layout>
      </Layout>
    );
  },
);
